import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import api from "../utils/api";

// Common country calling codes for supplier phone (flag + short code)
const COUNTRY_CODES = [
  { code: "+44", label: "UK", flag: "🇬🇧" },
  { code: "+353", label: "Ireland", flag: "🇮🇪" },
  { code: "+1", label: "US/Canada", flag: "🇺🇸" },
  { code: "+61", label: "Australia", flag: "🇦🇺" },
  { code: "+64", label: "New Zealand", flag: "🇳🇿" },
  { code: "+49", label: "Germany", flag: "🇩🇪" },
  { code: "+33", label: "France", flag: "🇫🇷" },
  { code: "+39", label: "Italy", flag: "🇮🇹" },
  { code: "+34", label: "Spain", flag: "🇪🇸" },
  { code: "+31", label: "Netherlands", flag: "🇳🇱" },
  { code: "+32", label: "Belgium", flag: "🇧🇪" },
  { code: "+41", label: "Switzerland", flag: "🇨🇭" },
  { code: "+43", label: "Austria", flag: "🇦🇹" },
  { code: "+46", label: "Sweden", flag: "🇸🇪" },
  { code: "+47", label: "Norway", flag: "🇳🇴" },
  { code: "+45", label: "Denmark", flag: "🇩🇰" },
  { code: "+358", label: "Finland", flag: "🇫🇮" },
  { code: "+48", label: "Poland", flag: "🇵🇱" },
  { code: "+351", label: "Portugal", flag: "🇵🇹" },
  { code: "+81", label: "Japan", flag: "🇯🇵" },
  { code: "+86", label: "China", flag: "🇨🇳" },
  { code: "+91", label: "India", flag: "🇮🇳" },
  { code: "+971", label: "UAE", flag: "🇦🇪" },
  { code: "+27", label: "South Africa", flag: "🇿🇦" },
  { code: "+55", label: "Brazil", flag: "🇧🇷" },
  { code: "+52", label: "Mexico", flag: "🇲🇽" },
];

const yesNoNaOptions = ["Yes", "No", "N/A"];

// Supplier risk dropdown options (per specification)
const SERVICE_CRITICALITY_OPTIONS = [
  "1 – Low",
  "2 – Moderate",
  "3 – High",
  "4 – Critical",
];
const TYPE_OF_INFO_HANDLED_OPTIONS = [
  "Public",
  "Internal / General",
  "Confidential",
  "Highly Confidential",
  "Regulated / Compliance-bound",
  "Personal Data",
  "Special Category Data",
];
const INFORMATION_CRITICALITY_OPTIONS = [
  "1 – Low",
  "2 – Medium",
  "3 – High",
  "4 – Critical",
];
const INFORMATION_SENSITIVITY_OPTIONS = [
  "1 – Public",
  "2 – Internal",
  "3 – Confidential",
  "4 – Highly Confidential / Restricted",
];
const OVERALL_RISK_OPTIONS = [
  "1 – Low",
  "2 – Medium",
  "3 – High",
  "4 – Very High",
];
const PERSONAL_INFO_PROCESSED_OPTIONS = [
  "No",
  "Yes – Personal Data",
  "Yes – Special Category Data",
  "Yes – Children's Data",
  "Yes – Financial Data",
  "Yes – Health Data",
];
const TYPE_OF_ACCESS_OPTIONS = [
  "1 – No system access",
  "2 – Standard user access",
  "3 – Elevated / Power user",
  "4 – Admin access",
  "5 – Privileged / Root access",
  "API / Machine-to-machine",
  "Physical access",
];

const SupplierForm = ({ supplier, onSubmit, onCancel }) => {
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    status: "Live",
    typeOfServiceProvided: "",
    supplierCompany: "",
    serviceDescription: "",
    individualName: "",
    supplierCountryCode: "+44",
    supplierContactNumber: "",
    supplierEmailContact: "",
    adminContact1: "",
    adminContact2: "",
    billingType: "",
    fixedDayRate: "",
    budgetDaysPerMonth: "",
    budgetGbp: "",
    creditTerms: "",
    contractStarts: null,
    contractEnds: null,
    terminationTerms: "",
    renewalCostExclVat: "",
    ndaSigned: "",
    linkToContract: "",
    isoCompliant: "",
    serviceCriticalityLevels: "",
    typeOfInfoHandled: "",
    informationCriticality: "",
    informationSensitivity: "",
    overallRisk: "",
    personalInfoProcessed: "",
    typeOfAccess: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get("/api/users");
        setUsers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.warn("Could not load users for admin contacts:", err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (supplier) {
      const rawPhone = supplier.supplierContactNumber || "";
      const match = rawPhone.match(/^(\+\d{1,4})\s*(.*)$/);
      const countryCode = match ? match[1] : "+44";
      const numberOnly = match
        ? match[2].replace(/\s+/g, " ").trim()
        : rawPhone;
      const norm = (v) =>
        !v || !v.trim()
          ? ""
          : yesNoNaOptions.includes(v)
            ? v
            : v.toUpperCase() === "YES"
              ? "Yes"
              : v.toUpperCase() === "NO"
                ? "No"
                : v.toUpperCase() === "N/A"
                  ? "N/A"
                  : v;
      setFormData({
        status:
          (supplier.status || "").toLowerCase() === "expired"
            ? "Expired"
            : "Live",
        typeOfServiceProvided: supplier.typeOfServiceProvided || "",
        supplierCompany: supplier.supplierCompany || "",
        serviceDescription: supplier.serviceDescription || "",
        individualName: supplier.individualName || "",
        supplierCountryCode: COUNTRY_CODES.some((c) => c.code === countryCode)
          ? countryCode
          : "+44",
        supplierContactNumber: numberOnly,
        supplierEmailContact: supplier.supplierEmailContact || "",
        adminContact1: supplier.adminContact1 || "",
        adminContact2: supplier.adminContact2 || "",
        billingType: supplier.billingType || "",
        fixedDayRate: supplier.fixedDayRate || "",
        budgetDaysPerMonth: supplier.budgetDaysPerMonth || "",
        budgetGbp: supplier.budgetGbp || "",
        creditTerms: supplier.creditTerms || "",
        contractStarts: supplier.contractStarts
          ? new Date(supplier.contractStarts)
          : null,
        contractEnds: supplier.contractEnds
          ? new Date(supplier.contractEnds)
          : null,
        terminationTerms: supplier.terminationTerms || "",
        renewalCostExclVat: supplier.renewalCostExclVat || "",
        ndaSigned: norm(supplier.ndaSigned || ""),
        linkToContract: supplier.linkToContract || "",
        isoCompliant: norm(supplier.isoCompliant || ""),
        serviceCriticalityLevels: supplier.serviceCriticalityLevels || "",
        typeOfInfoHandled: supplier.typeOfInfoHandled || "",
        informationCriticality: supplier.informationCriticality || "",
        informationSensitivity: supplier.informationSensitivity || "",
        overallRisk: supplier.overallRisk || "",
        personalInfoProcessed: supplier.personalInfoProcessed || "",
        typeOfAccess: supplier.typeOfAccess || "",
      });
    }
  }, [supplier]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!(formData.supplierCompany || "").trim())
      next.supplierCompany = "Supplier company is required";
    const phoneDigits = (formData.supplierContactNumber || "").replace(
      /\D/g,
      "",
    );
    if (
      phoneDigits.length > 0 &&
      (phoneDigits.length < 6 || phoneDigits.length > 15)
    )
      next.supplierContactNumber = "Enter a valid phone number (6–15 digits)";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const fullPhone =
      formData.supplierContactNumber && formData.supplierCountryCode
        ? `${formData.supplierCountryCode} ${(formData.supplierContactNumber || "").trim()}`
        : formData.supplierContactNumber || "";
    const { supplierCountryCode, ...rest } = formData;
    const payload = {
      ...rest,
      supplierContactNumber: fullPhone,
      contractStarts: formData.contractStarts
        ? formData.contractStarts.toISOString().split("T")[0]
        : null,
      contractEnds: formData.contractEnds
        ? formData.contractEnds.toISOString().split("T")[0]
        : null,
    };
    onSubmit(payload);
  };

  return (
      <form onSubmit={handleSubmit}>
        <Box pr={1} pt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel id="supplier-status-label">Status</InputLabel>
                <Select
                  labelId="supplier-status-label"
                  label="Status"
                  value={formData.status || "Live"}
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  <MenuItem value="Live">Live</MenuItem>
                  <MenuItem value="Expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Type of service provided"
                value={formData.typeOfServiceProvided}
                onChange={(e) =>
                  handleChange("typeOfServiceProvided", e.target.value)
                }
                placeholder="e.g. People, Legal & Compliance"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Supplier Company"
                value={formData.supplierCompany}
                onChange={(e) =>
                  handleChange("supplierCompany", e.target.value)
                }
                error={!!errors.supplierCompany}
                helperText={errors.supplierCompany}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Service description"
                value={formData.serviceDescription}
                onChange={(e) =>
                  handleChange("serviceDescription", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Individual Name"
                value={formData.individualName}
                onChange={(e) => handleChange("individualName", e.target.value)}
                multiline
                minRows={1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <FormControl size="small" style={{ minWidth: 132 }}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    label="Country"
                    value={formData.supplierCountryCode || "+44"}
                    onChange={(e) =>
                      handleChange("supplierCountryCode", e.target.value)
                    }
                    renderValue={(value) => {
                      const c = COUNTRY_CODES.find((x) => x.code === value);
                      return c ? `${c.flag} ${c.code}` : value;
                    }}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        <Box component="span" mr={1}>
                          {c.flag}
                        </Box>
                        {c.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  label="Supplier contact number"
                  placeholder="e.g. 7700 900123"
                  value={formData.supplierContactNumber}
                  onChange={(e) =>
                    handleChange("supplierContactNumber", e.target.value)
                  }
                  error={!!errors.supplierContactNumber}
                  helperText={errors.supplierContactNumber}
                  inputProps={{ inputMode: "tel", maxLength: 20 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Supplier Email/contact"
                value={formData.supplierEmailContact}
                onChange={(e) =>
                  handleChange("supplierEmailContact", e.target.value)
                }
                multiline
                minRows={1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="admin-contact-1-label">
                  Admin user/contact 1
                </InputLabel>
                <Select
                  labelId="admin-contact-1-label"
                  label="Admin user/contact 1"
                  value={formData.adminContact1 || ""}
                  onChange={(e) =>
                    handleChange("adminContact1", e.target.value)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {users.map((u) => {
                    const name =
                      `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                      u.email ||
                      "";
                    return (
                      <MenuItem key={u.id} value={name}>
                        {name}
                        {u.email ? ` (${u.email})` : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="admin-contact-2-label">
                  Admin user/contact 2
                </InputLabel>
                <Select
                  labelId="admin-contact-2-label"
                  label="Admin user/contact 2"
                  value={formData.adminContact2 || ""}
                  onChange={(e) =>
                    handleChange("adminContact2", e.target.value)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {users.map((u) => {
                    const name =
                      `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                      u.email ||
                      "";
                    return (
                      <MenuItem key={u.id} value={name}>
                        {name}
                        {u.email ? ` (${u.email})` : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Billing Type</InputLabel>
                <Select
                  value={formData.billingType || ""}
                  label="Billing Type"
                  onChange={(e) => handleChange("billingType", e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="Recurring">Recurring</MenuItem>
                  <MenuItem value="One off">One off</MenuItem>
                  <MenuItem value="Ad hoc">Ad hoc</MenuItem>
                  <MenuItem value="Annual">Annual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Fixed Day Rate"
                value={formData.fixedDayRate}
                onChange={(e) => handleChange("fixedDayRate", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Budget Days Per Month"
                value={formData.budgetDaysPerMonth}
                onChange={(e) =>
                  handleChange("budgetDaysPerMonth", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Budget (£)"
                value={formData.budgetGbp}
                onChange={(e) => handleChange("budgetGbp", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Credit Terms"
                value={formData.creditTerms}
                onChange={(e) => handleChange("creditTerms", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Contract Starts"
                value={formData.contractStarts}
                onChange={(d) => handleChange("contractStarts", d)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Contract Ends"
                value={formData.contractEnds}
                onChange={(d) => handleChange("contractEnds", d)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Termination terms"
                value={formData.terminationTerms}
                onChange={(e) =>
                  handleChange("terminationTerms", e.target.value)
                }
                multiline
                minRows={1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Renewal cost (excl. VAT)"
                value={formData.renewalCostExclVat}
                onChange={(e) =>
                  handleChange("renewalCostExclVat", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>NDA signed</InputLabel>
                <Select
                  label="NDA signed"
                  value={formData.ndaSigned || ""}
                  onChange={(e) => handleChange("ndaSigned", e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {yesNoNaOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Link to the contract"
                value={formData.linkToContract}
                onChange={(e) => handleChange("linkToContract", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>ISO compliant</InputLabel>
                <Select
                  label="ISO compliant"
                  value={formData.isoCompliant || ""}
                  onChange={(e) => handleChange("isoCompliant", e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {yesNoNaOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Service criticality level</InputLabel>
                <Select
                  label="Service criticality level"
                  value={formData.serviceCriticalityLevels || ""}
                  onChange={(e) =>
                    handleChange("serviceCriticalityLevels", e.target.value)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {SERVICE_CRITICALITY_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type of information handled</InputLabel>
                <Select
                  label="Type of information handled"
                  value={formData.typeOfInfoHandled || ""}
                  onChange={(e) =>
                    handleChange("typeOfInfoHandled", e.target.value)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {TYPE_OF_INFO_HANDLED_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Information criticality</InputLabel>
                <Select
                  label="Information criticality"
                  value={formData.informationCriticality || ""}
                  onChange={(e) =>
                    handleChange("informationCriticality", e.target.value)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {INFORMATION_CRITICALITY_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Information sensitivity</InputLabel>
                <Select
                  label="Information sensitivity"
                  value={formData.informationSensitivity || ""}
                  onChange={(e) =>
                    handleChange("informationSensitivity", e.target.value)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {INFORMATION_SENSITIVITY_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Overall risk</InputLabel>
                <Select
                  label="Overall risk"
                  value={formData.overallRisk || ""}
                  onChange={(e) => handleChange("overallRisk", e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {OVERALL_RISK_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Personal information processed</InputLabel>
                <Select
                  label="Personal information processed"
                  value={formData.personalInfoProcessed || ""}
                  onChange={(e) =>
                    handleChange("personalInfoProcessed", e.target.value)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {PERSONAL_INFO_PROCESSED_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type of access</InputLabel>
                <Select
                  label="Type of access"
                  value={formData.typeOfAccess || ""}
                  onChange={(e) => handleChange("typeOfAccess", e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {TYPE_OF_ACCESS_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="contained">
            {supplier ? "Save" : "Add Supplier"}
          </Button>
        </Box>
      </form>
  );
};

export default SupplierForm;
